import { reactive, markRaw, nextTick } from 'vue';

/**
 * Global modal state - reactive object that ModalContainer watches
 */
export const modalState = reactive({
    // Stack of modals (supports nested modals)
    stack: []
});

/**
 * Show a modal and return a promise.
 * @param {object} component
 * @param {object} props
 * @param {object} options
 * @param {boolean} [options.isFormModal=false]  True for user content modals (set by Modal.show()).
 *                                               False for system modals (confirm/alert/prompt).
 */
export function showModal(component, props = {}, { isFormModal = false } = {}) {
    return new Promise((resolve, reject) => {
        const modal = {
            id: Date.now(),
            component: markRaw(component),
            props,
            isFormModal,
            visible: false,
            resolve,
            reject
        };
        modalState.stack.push(modal);
        // Delay visibility to allow the component to mount first,
        // so the b-modal transition animation plays on open
        nextTick(() => {
            const m = modalState.stack.find(m => m.id === modal.id);
            if (m) m.visible = true;
        });
    });
}

/**
 * Close the top modal with a result
 */
export function closeModal(id, result, rejected = false) {
    const index = modalState.stack.findIndex(m => m.id === id);
    if (index !== -1) {
        const modal = modalState.stack[index];
        modalState.stack.splice(index, 1);
        if (rejected) {
            modal.reject(result);
        } else {
            modal.resolve(result);
        }
    }
}

/**
 * Close all modals
 */
export function closeAllModals() {
    while (modalState.stack.length > 0) {
        const modal = modalState.stack.pop();
        modal.reject();
    }
}

/**
 * Returns true if any user content modal (not a system modal) is currently open.
 */
export function hasOpenFormModal() {
    return modalState.stack.some(m => m.isFormModal);
}
